import { produce } from "immer";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { assign, fromPromise, setup } from "xstate";

import { generateShortUuid } from "@moment/api-collab";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { buildInputRules } from "~/components/canvas/easel/prosemirror/rules";
import { getDocumentDirPath, mkdir } from "~/data/filesystem";
import { getDocsApiUrl } from "~/state/api/docsapi";
import { filesystemApi } from "~/state/api/filesystem";
import { sqliteApi } from "~/state/api/sqlite";
import { tauriApi } from "~/state/api/tauri";

import type { DocumentListItem, DocumentMetadata } from "../../types";
import { authenticationActorRef, waitForAccessTokenSilently } from "../authentication";
import { initRepoMachine } from "../git/init-repo.machine";
import { createPageMachine } from "../pages/create-page.machine";

const logger = new Logger("createDocumentMachine");

// const debuglog = (_msg, ...args) => {
// 	console.log(`[create document machine] ${_msg}`, ...args);
// };
const debuglog = (_msg, ..._) => {};

const getRemoteUrl = (workspaceName: string, documentId: string, branch: string) => {
	return `${getDocsApiUrl()}/api/v1/repos/${workspaceName}/${documentId}/archive/${branch}.tar.gz`;
};

export const createDocumentMachine = setup({
	types: {
		context: {} as {
			documentId: string;
			directoryPath?: string;
			templateWorkspaceId?: string;
			templateId?: string;
			documentMetadata: DocumentMetadata;
			body: string | undefined;
			bodyType: "html" | "text" | undefined;
		},
		input: {} as {
			templateWorkspaceId?: string;
			templateId?: string;
			title?: string;
			body?: string;
			bodyType?: "html" | "text" | undefined;
		},
		output: {} as DocumentListItem,
	},
	actors: {
		getDirectoryPath: fromPromise<string, { documentId: string }>(async ({ input }) => {
			return await getDocumentDirPath(input.documentId);
		}),
		createDirectory: fromPromise<string, { documentId: string }>(async ({ input }) => {
			const directoryPath = await getDocumentDirPath(input.documentId);
			await mkdir(directoryPath, {
				recursive: true,
			});

			return directoryPath;
		}),
		createPage: createPageMachine,
		setPageBody: fromPromise<
			void,
			{
				documentPath: string;
				pageId: string;
				bodyType: "html" | "text" | undefined;
				body: string | undefined;
			}
		>(async ({ input }) => {
			if (!input.body) {
				return;
			}

			// create a temporary prosemirror EditorView and use it
			// to paste the contents so it transforms correctly

			const transformer = new EditorView(null, {
				state: EditorState.create({
					schema,
					plugins: [buildInputRules(schema)],
				}),
			});

			if (input.bodyType === "html") {
				transformer.pasteHTML(input.body);
			} else {
				transformer.pasteText(input.body);
			}

			await filesystemApi.page.contents.write(
				input.documentPath,
				input.pageId,
				transformer.state.doc
			);
		}),
		writeMetadataFile: fromPromise<void, { path: string; contents: DocumentMetadata }>(
			({ input }) => filesystemApi.document.metadata.write(input.path, input.contents)
		),
		initFromArchive: fromPromise<void, { path: string; remoteUrl: string }>(
			async ({ input }) => {
				const token = await waitForAccessTokenSilently(authenticationActorRef);
				await tauriApi.initFromArchive({
					path: input.path,
					remoteUrl: input.remoteUrl,
					token,
				});
			}
		),
		loadDocumentMetadata: fromPromise<DocumentMetadata | undefined, { path: string }>(
			({ input }) => {
				return filesystemApi.document.metadata.read(input.path);
			}
		),
		initRepo: initRepoMachine,
		updateCache: fromPromise<void, DocumentListItem>(({ input }) =>
			sqliteApi.documents.upsert(input)
		),
	},
}).createMachine({
	id: "create-document",
	entry: [() => debuglog("Creating new document")],
	exit: [() => debuglog("Finished creating document")],
	context: ({ input }) => {
		// Generate the new document ID
		const documentId = generateShortUuid();

		return {
			documentId,
			templateWorkspaceId: input.templateWorkspaceId,
			templateId: input.templateId,
			documentMetadata: {
				id: documentId,
				title: input.title ?? "",
				created: new Date(),
				pagesMetadata: [], // Leaving pages array empty to start, we will add a stub page later in this flow
				schemaVersion: 1,
			},
			body: input.body,
			bodyType: input.bodyType,
		};
	},
	initial: "preparing",
	states: {
		preparing: {
			always: [
				{
					target: "cloningTemplate",
					guard: ({ context }) =>
						context.templateWorkspaceId !== undefined &&
						context.templateId !== undefined,
				},
				{ target: "creatingEmptyDocument" },
			],
		},

		creatingEmptyDocument: {
			initial: "creatingDirectory",
			states: {
				creatingDirectory: {
					invoke: {
						src: "createDirectory",
						input: ({ context }) => ({ documentId: context.documentId }),
						onError: {
							actions: ({ event }) => {
								logger.error("Failed to create directory", {
									event,
								});
								throw new Error("Failed to create directory", {
									cause: event.error,
								});
							},
							target: "#create-document.done",
						},
						onDone: {
							target: "creatingPage",
							actions: assign(({ context, event }) =>
								produce(context, (draft) => {
									draft.directoryPath = event.output;
								})
							),
						},
					},
				},

				creatingPage: {
					invoke: {
						src: "createPage",
						input: ({ context }) => ({
							documentId: context.documentId,
							documentPath: context.directoryPath!,
							pageMetadata: {
								slug: "index",
							},
						}),
						onError: {
							actions: ({ event }) => {
								logger.error("Failed to create page", {
									event,
								});
								throw new Error("Failed to create page", {
									cause: event.error,
								});
							},
							target: "#create-document.done",
						},
						onDone: {
							target: "settingPageBody",
							actions: assign(({ context, event }) =>
								produce(context, (draft) => {
									draft.documentMetadata.pagesMetadata.push(
										event.output.pageMetadata
									);

									return draft;
								})
							),
						},
					},
				},

				settingPageBody: {
					invoke: {
						src: "setPageBody",
						input: ({ context }) => ({
							documentPath: context.directoryPath!,
							pageId: context.documentMetadata.pagesMetadata[0]!.id,
							bodyType: context.bodyType,
							body: context.body,
						}),
						onError: {
							actions: ({ event }) => {
								logger.error("Error creating initial page contents", {
									event,
								});
								throw new Error("Error creating initial page contents", {
									cause: event.error,
								});
							},
							target: "#create-document.done",
						},
						onDone: {
							target: "#create-document.writingMetadataFile",
						},
					},
				},
			},
		},

		cloningTemplate: {
			initial: "gettingDirectoryPath",
			states: {
				gettingDirectoryPath: {
					invoke: {
						src: "getDirectoryPath",
						input: ({ context }) => ({ documentId: context.documentId }),
						onDone: {
							actions: assign(({ event }) => ({
								directoryPath: event.output,
							})),
							target: "downloadingTemplate",
						},
						onError: {
							actions: ({ event }) => {
								logger.error("Failed to get directory path", {
									event,
								});
								throw new Error("Failed to get directory path", {
									cause: event.error,
								});
							},
							target: "#create-document.done",
						},
					},
				},

				downloadingTemplate: {
					invoke: {
						src: "initFromArchive",
						input: ({ context }) => ({
							path: context.directoryPath!,
							remoteUrl: getRemoteUrl(
								context.templateWorkspaceId!,
								context.templateId!,
								"main"
							),
						}),
						onDone: {
							target: "loadingDocumentMetadataFromTemplate",
						},
						onError: {
							actions: ({ event }) => {
								throw new Error("Failed to init document from template archive", {
									cause: event.error,
								});
							},
							target: "#create-document.done",
						},
					},
				},

				loadingDocumentMetadataFromTemplate: {
					invoke: {
						src: "loadDocumentMetadata",
						input: ({ context }) => ({
							path: context.directoryPath!,
						}),
						onDone: {
							actions: assign(({ context, event }) => {
								if (event.output === undefined) {
									throw new Error(
										"Failed to load document metadata from cloned template"
									);
								}

								const documentMetadata = structuredClone(event.output);
								documentMetadata.id = context.documentId;
								documentMetadata.title = "";
								documentMetadata.created = new Date();

								return {
									documentMetadata,
								};
							}),
							target: "#create-document.writingMetadataFile",
						},
						onError: {
							actions: ({ event }) => {
								logger.error(
									"Failed to load document metadata from cloned template",
									{
										event,
									}
								);
								throw new Error(
									"Failed to load document metadata from cloned template",
									{
										cause: event.error,
									}
								);
							},
							target: "#create-document.done",
						},
					},
				},
			},
		},

		writingMetadataFile: {
			invoke: {
				src: "writeMetadataFile",
				input: ({ context }) => ({
					path: context.directoryPath!,
					contents: context.documentMetadata,
				}),
				onError: {
					actions: ({ event }) => {
						logger.error("Failed to write metadata file", {
							event,
						});
						throw new Error("Failed to write metadata file", {
							cause: event.error,
						});
					},
					target: "#create-document.done",
				},
				onDone: {
					target: "initializingGitRepository",
				},
			},
		},

		initializingGitRepository: {
			invoke: {
				src: "initRepo",
				input: ({ context }) => ({ path: context.directoryPath! }),
				onError: {
					actions: ({ event }) => {
						throw new Error("Failed to initialize git repository", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "updatingCache",
				},
			},
		},

		updatingCache: {
			invoke: {
				src: "updateCache",
				input: ({ context }) => ({
					id: context.documentId,
					path: context.directoryPath!,
					title: context.documentMetadata.title,
					createdAt: context.documentMetadata.created,
					updatedAt: context.documentMetadata.created,
					template: false, // A new document can not yet be a template
					workspaces: [],
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Failed to update cache", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "done",
				},
			},
		},

		done: {
			type: "final",
		},
	},
	output: ({ context }) =>
		({
			id: context.documentId,
			path: context.directoryPath!,
			title: context.documentMetadata.title,
			createdAt: context.documentMetadata.created,
			updatedAt: context.documentMetadata.created,
			template: false,
			workspaces: [],
		}) as DocumentListItem,
});
